<script setup lang="ts">
import ArrowRight from '~/assets/icon/arrow-right.svg'
import CloseIcon from '~/assets/icon/close-2.svg'
import { getNewsBelongTabType, getNewsDetailPath, getThirdPartyArticleList } from '~/api/news'
import { formatRelativeTime } from '~/utils/appTime'

type ThirdPartyArticle = Record<string, unknown>
type ThirdPartyArticlesResult = {
  total?: number
  items?: unknown[] | null
}

type ExchangeNewsItem = {
  id: string
  title: string
  summary: string
  date: string
  tag: string
  image: string
  belongTabType: number
  urlPath: string
}

const props = defineProps<{
  articles?: ThirdPartyArticlesResult | null
  exchangeName?: string
  exchangeCode?: string
}>()

const { localePath, localeMeta, t } = useAppI18n()
const { dir } = useRtl()
const route = useRoute()
const { setBreadcrumb } = useLsBreadcrumb()
const { lock: lockPageScroll, unlock: unlockPageScroll } = useStickyDialogScrollLock()
const isNewsDialogOpen = ref(false)
const dialogArticles = ref<ThirdPartyArticle[]>([])
const dialogTotal = ref(0)
const nextPageIndex = ref(1)
const isLoadingMoreNews = ref(false)
const hasReachedNewsEnd = ref(false)

const NEWS_PAGE_SIZE = 10

watch(isNewsDialogOpen, (isOpen) => {
  if (isOpen) {
    lockPageScroll()
    return
  }

  unlockPageScroll()
})

onBeforeUnmount(() => {
  unlockPageScroll()
})

const getText = (article: ThirdPartyArticle, ...keys: string[]) => {
  for (const key of keys) {
    const value = article[key]
    if (typeof value === 'string' && value.trim()) return value.trim()
  }

  return ''
}

const newsItems = computed<ExchangeNewsItem[]>(() =>
  dialogArticles.value.flatMap((value, index) => {
    if (!value || typeof value !== 'object') return []
    const article = value as ThirdPartyArticle
    const urlPath = getText(article, 'urlPath', 'url', 'link')
    const title = getText(article, 'title', 'articleTitle', 'name')
    const tags = Array.isArray(article.tags) ? article.tags : []
    const firstTag = tags[0] && typeof tags[0] === 'object' ? (tags[0] as ThirdPartyArticle) : null

    if (!title) return []

    return [
      {
        id: getText(article, 'code', 'artcode') || `exchange-news-${index}`,
        title,
        summary: getText(article, 'content'),
        date: formatRelativeTime(article.wiki_timestamp as number | undefined, localeMeta.language, { translate: t }),
        tag: getText(firstTag || {}, 'tagname', 'name') || getText(article, 'categoryName', 'category', 'tag'),
        image: getText(article, 'imgurl', 'image', 'cover'),
        belongTabType: getNewsBelongTabType(article),
        urlPath,
      },
    ]
  }),
)

const displayedNewsItems = computed(() => newsItems.value.slice(0, 3))
const newsTotal = computed(() => dialogTotal.value || newsItems.value.length)
const hasMoreNews = computed(() => newsTotal.value > displayedNewsItems.value.length)
const hasMoreDialogNews = computed(() => !hasReachedNewsEnd.value && dialogTotal.value > dialogArticles.value.length)

/** 将详情接口预加载的新闻作为弹窗的第一页，滚动加载从第二页开始。 */
const resetDialogArticles = () => {
  dialogArticles.value = (props.articles?.items || []).filter((article): article is ThirdPartyArticle =>
    Boolean(article && typeof article === 'object'),
  ) as ThirdPartyArticle[]
  dialogTotal.value = props.articles?.total ?? dialogArticles.value.length
  nextPageIndex.value = 2
  hasReachedNewsEnd.value = dialogTotal.value === 0
}

watch(
  () => props.articles,
  () => {
    resetDialogArticles()
  },
  { immediate: true },
)

/** 滚动至弹窗列表底部时，加载下一页关联新闻。 */
const loadMoreNews = async () => {
  if (!props.exchangeCode || isLoadingMoreNews.value || !hasMoreDialogNews.value) return

  isLoadingMoreNews.value = true
  try {
    const result = await getThirdPartyArticleList({
      code: props.exchangeCode,
      pageIndex: nextPageIndex.value,
      pageSize: NEWS_PAGE_SIZE,
      isHoneycomb: false,
    })
    const items = Array.isArray(result.items) ? result.items : []
    const knownArticleIds = new Set(
      dialogArticles.value.map((article) => getText(article, 'code', 'artcode', 'urlPath')),
    )
    const newItems = items.filter((article) => {
      const articleId = getText(article, 'code', 'artcode', 'urlPath')
      return !articleId || !knownArticleIds.has(articleId)
    })

    dialogArticles.value.push(...newItems)
    dialogTotal.value = result.total ?? dialogTotal.value
    nextPageIndex.value += 1
    // 服务端返回 total=0 或空页时停止加载，避免滚动触底后重复请求。
    hasReachedNewsEnd.value = result.total === 0 || items.length === 0
  } finally {
    isLoadingMoreNews.value = false
  }
}

/** 仅在接近列表底部时触发分页请求，避免每次滚动都发请求。 */
const handleNewsDialogScroll = (event: Event) => {
  const container = event.currentTarget as HTMLElement
  if (container.scrollTop + container.clientHeight >= container.scrollHeight - 80) {
    void loadMoreNews()
  }
}

const handleNewsClick = (item: ExchangeNewsItem) => {
  if (!item.urlPath || import.meta.server) return

  if (props.exchangeName) {
    setBreadcrumb(item.urlPath, {
      label: props.exchangeName,
      route: route.fullPath,
    })
  }
  window.open(localePath(getNewsDetailPath(item.belongTabType, item.urlPath)), '_blank', 'noopener,noreferrer')
}
const handleMoreNewsClick = () => {
  if (!hasMoreNews.value) return

  resetDialogArticles()
  isNewsDialogOpen.value = true
}
const closeNewsDialog = () => {
  isNewsDialogOpen.value = false
}
</script>

<template>
  <section class="exchange-news" :class="{ 'is-rtl': dir === 'rtl' }" :dir="dir" aria-labelledby="exchange-news-title">
    <header class="exchange-news-header" @click="handleMoreNewsClick">
      <h2 id="exchange-news-title" class="exchange-news-title">{{ t('80338') }}</h2>
      <div v-if="hasMoreNews" class="exchange-news-header-action">
        <span class="exchange-news-total">{{ newsTotal }}</span>
        <button type="button" class="exchange-news-more" aria-label="Show all news">
          <ArrowRight class="size-[20px] rtl:rotate-180" aria-hidden="true" />
        </button>
      </div>
    </header>

    <article
      v-for="(item, index) in displayedNewsItems"
      :key="item.id"
      class="exchange-news-item cursor-pointer"
      :class="{ 'exchange-news-item-last': index === displayedNewsItems.length - 1 }"
      @click="handleNewsClick(item)"
    >
      <div class="exchange-news-item-main">
        <div class="exchange-news-copy">
          <h3 class="exchange-news-item-title">{{ item.title }}</h3>
          <p class="exchange-news-item-summary">{{ item.summary }}</p>
        </div>
        <div class="exchange-news-image-wrap">
          <CommonImage
            :src="item.image"
            :alt="item.title"
            class="size-full object-cover"
            width="139"
            height="78"
            loading="eager"
            object-fit="cover"
          />
        </div>
      </div>
      <div class="exchange-news-meta">
        <span class="exchange-news-meta-group">
          <span class="size-[14px] text-center text-[#7d818a]" aria-hidden="true">•</span>
          {{ item.date }}
        </span>
        <span class="exchange-news-divider" aria-hidden="true"></span>
        <span>{{ item.tag }}</span>
      </div>
    </article>

    <Teleport to="body">
      <div
        v-if="isNewsDialogOpen"
        class="fixed inset-0 z-[1200] flex items-center justify-center bg-[rgba(11,18,40,0.36)] p-[24px] max-[799px]:p-[12px]"
        @click.self="closeNewsDialog"
      >
        <section
          class="w-[760px] max-w-[calc(100vw_-_48px)] overflow-hidden rounded-[28px] bg-white shadow-[0_24px_80px_rgba(8,19,62,0.18)] max-[799px]:max-w-[calc(100vw_-_24px)] max-[799px]:rounded-[24px]"
          role="dialog"
          aria-modal="true"
          aria-labelledby="exchange-news-dialog-title"
        >
          <header
            class="mx-[32px] flex items-center justify-between gap-[8px] pt-[32px] pb-[16px] shadow-[inset_0_-0.5px_0_rgba(5,21,36,0.06)] max-[799px]:px-[20px] max-[799px]:pt-[24px]"
          >
            <h2
              id="exchange-news-dialog-title"
              class="m-0 overflow-hidden text-[20px] leading-[28px] font-semibold text-ellipsis whitespace-nowrap text-[#292a2e]"
            >
              {{ t('80338') }}
            </h2>
            <button
              type="button"
              class="size-[32px] cursor-pointer rounded-[999px] transition-opacity hover:opacity-80"
              :aria-label="t('83711')"
              @click="closeNewsDialog"
            >
              <CloseIcon class="size-full" aria-hidden="true" />
            </button>
          </header>

          <div
            class="max-h-[min(640px,calc(100vh_-_160px))] overflow-y-auto px-[32px] pt-[20px] pb-[32px] max-[799px]:max-h-[calc(100vh_-_120px)] max-[799px]:px-[20px] max-[799px]:pt-[0] max-[799px]:pb-[24px]"
            @scroll="handleNewsDialogScroll"
          >
            <article
              v-for="(item, index) in newsItems"
              :key="`${item.id}-${index}`"
              class="exchange-news-dialog-item cursor-pointer py-[16px] shadow-[inset_0_-0.5px_0_rgba(5,21,36,0.06)] hover:bg-[#f8f8f8]"
              :class="{ 'shadow-none': index === newsItems.length - 1 }"
              @click="handleNewsClick(item)"
            >
              <div class="flex min-w-0 gap-[12px]">
                <div class="min-w-0 flex-1">
                  <h3
                    class="m-0 overflow-hidden text-[18px] leading-[26px] font-semibold text-ellipsis whitespace-nowrap text-[#292a2e] max-[799px]:text-[16px] max-[799px]:leading-[24px]"
                  >
                    {{ item.title }}
                  </h3>
                  <p class="mt-[8px] mb-[0] line-clamp-2 text-[14px] leading-[22px] text-[#505258]">
                    {{ item.summary }}
                  </p>
                  <div class="mt-[12px] flex items-center gap-[8px] text-[12px] leading-[18px] text-[#7d818a]">
                    <span class="inline-flex items-center gap-[4px]">
                      <span class="size-[14px] text-center text-[#7d818a]" aria-hidden="true">•</span>
                      {{ item.date }}
                    </span>
                    <span class="h-[10px] w-[1px] bg-[rgba(11,18,40,0.14)]" aria-hidden="true"></span>
                    <span>{{ item.tag }}</span>
                  </div>
                </div>
                <CommonImage
                  :src="item.image"
                  :alt="item.title"
                  class="h-[78px] w-[139px] flex-none rounded-[8px] object-cover max-[799px]:h-[62px] max-[799px]:w-[110px]"
                  width="139"
                  height="78"
                  loading="lazy"
                  object-fit="cover"
                />
              </div>
            </article>
            <p v-if="isLoadingMoreNews" class="m-0 pt-[16px] text-center text-[14px] leading-[22px] text-[#7d818a]">
              {{ t('83713') }}
            </p>
          </div>
        </section>
      </div>
    </Teleport>
  </section>
</template>

<style scoped lang="less">
.exchange-news {
  width: 100%;
  padding: 24px 24px 8px;
  border: 1px solid rgba(11, 18, 40, 0.14);
  border-radius: 12px;
  background: #fff;
  color: #292a2e;
}

.exchange-news-header {
  display: flex;
  width: 100%;
  align-items: center;
  gap: 16px;
  padding-bottom: 16px;
  border-bottom: 0.5px solid rgba(5, 21, 36, 0.06);
}

.exchange-news-title {
  min-width: 0;
  flex: 1;
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  line-height: 28px;
}

.exchange-news-header-action {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 8px;
}

.exchange-news-total {
  color: #7d818a;
  font-size: 14px;
  line-height: 22px;
}

.exchange-news-more {
  border: 0;
  background: transparent;
  display: flex;
  width: 20px;
  height: 20px;
  flex: 0 0 20px;
  align-items: center;
  justify-content: center;
  color: #7d818a;
  cursor: pointer;
  transition: opacity 0.2s ease;
}

.exchange-news-more:hover {
  opacity: 0.7;
}

.exchange-news-item {
  display: flex;
  width: 100%;
  flex-direction: column;
  gap: 12px;
  padding: 20px 0 16px;
  border-bottom: 0.5px solid rgba(5, 21, 36, 0.06);
  text-decoration: none;
  color: inherit;
  transition: background-color 0.2s ease;
}

.exchange-news-item:hover {
  background: #f8f8f8;
}

.exchange-news-item-last {
  padding-bottom: 16px;
  border-bottom: 0;
}

.exchange-news-item-main {
  display: flex;
  width: 100%;
  min-width: 0;
  align-items: flex-start;
  gap: 12px;
}

.exchange-news-copy {
  min-width: 0;
  flex: 1;
}

.exchange-news-item-title,
.exchange-news-item-summary {
  overflow: hidden;
  margin: 0;
  text-overflow: ellipsis;
}

.exchange-news-item-title {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 1;
  font-size: 18px;
  font-weight: 600;
  line-height: 26px;
  white-space: normal;
}

.exchange-news-item-summary {
  display: -webkit-box;
  margin-top: 8px;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  color: #505258;
  font-size: 14px;
  line-height: 22px;
}

.exchange-news-image-wrap {
  width: 138.667px;
  height: 78px;
  flex: 0 0 138.667px;
  overflow: hidden;
  border-radius: 8px;
}

.exchange-news-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #7d818a;
  font-size: 12px;
  line-height: 18px;
}

.exchange-news-meta-group {
  display: flex;
  align-items: center;
  gap: 4px;
}

.exchange-news-divider {
  width: 1px;
  height: 10px;
  border-left: 1px solid rgba(11, 18, 40, 0.14);
}

.exchange-news.is-rtl .exchange-news-divider {
  border-right: 1px solid rgba(11, 18, 40, 0.14);
  border-left: 0;
}

@media screen and (max-width: 799px) {
  .exchange-news {
    padding-right: 16px;
    padding-left: 16px;
  }

  .exchange-news-image-wrap {
    width: 110px;
    height: 62px;
    flex-basis: 110px;
  }
}
</style>
